import { rtkApi } from '@/shared/api';
import { SuccessApiResponse } from '@/shared/types/common';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import {
  IActivateAccountRequest,
  IActivationDetails,
  IAuthByEmailProps,
  IAuthResponse,
  IConfirmEmailRequest,
  IConfirmEmailResponse,
  ISetPasswordRequest,
  IResendVerificationEmailRequest,
  IResendVerificationEmailResponse,
  IForgotPasswordRequest,
  IForgotPasswordResponse,
  IResetPasswordRequest,
  ILogoutRequest,
  ILogoutResponse,
} from './types';

const saveAuthData = (data: IAuthResponse): IAuthResponse => {
  if (data.jwt) {
    LocalStorageService.setAccessToken(data.jwt);
  }

  const refreshToken = (data as any).refreshToken || (data as any).refresh_token;
  if (refreshToken) {
    LocalStorageService.setRefreshToken(refreshToken);
  }

  if (typeof window !== 'undefined') {
    const shortUser = {
      userId: data.user.id.toString(),
      firstName: data.user.firstName,
      lastName: data.user.lastName,
      email: data.user.email,
    };
    const userJson = JSON.stringify(shortUser);
    sessionStorage.setItem('user', userJson);
    localStorage.setItem('user', userJson);
    sessionStorage.setItem('isAuth', 'true');
    localStorage.setItem('isAuth', 'true');
  }

  return data;
};

export const authApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    signIn: build.mutation<IAuthResponse, IAuthByEmailProps>({
      query: ({ email, password }) => ({
        url: '/auth/login',
        method: 'POST',
        body: { email, password, clientType: 'lms' },
      }),
      transformResponse: (response: SuccessApiResponse<IAuthResponse>) => saveAuthData(response.data),
    }),
    confirmEmail: build.mutation<IConfirmEmailResponse, IConfirmEmailRequest>({
      query: ({ token }) => ({
        url: '/auth/student/confirm-email',
        method: 'POST',
        body: { token },
      }),
      transformResponse: (response: SuccessApiResponse<IConfirmEmailResponse>) => response.data,
    }),
    setPassword: build.mutation<IAuthResponse, ISetPasswordRequest>({
      query: ({ token, password }) => ({
        url: '/auth/set-password',
        method: 'POST',
        body: { token, password },
      }),
      transformResponse: (response: SuccessApiResponse<IAuthResponse>) =>
        saveAuthData(response.data),
    }),
    // Ссылка активации из письма: учётку завели за человека (покупка курса или сотрудник),
    // пароля у неё нет. Сессии тут ещё нет, поэтому 401/403 не уводят на /login
    getActivation: build.query<IActivationDetails, string>({
      query: (token) => ({
        url: '/auth/student/activate',
        params: { token },
      }),
      extraOptions: { skipAuthRedirect: true },
      transformResponse: (response: SuccessApiResponse<IActivationDetails>) => response.data,
    }),
    activateAccount: build.mutation<IAuthResponse, IActivateAccountRequest>({
      query: (body) => ({
        url: '/auth/student/activate',
        method: 'POST',
        body,
      }),
      extraOptions: { skipAuthRedirect: true },
      transformResponse: (response: SuccessApiResponse<IAuthResponse>) =>
        saveAuthData(response.data),
    }),
    resendVerificationEmail: build.mutation<
      IResendVerificationEmailResponse,
      IResendVerificationEmailRequest
    >({
      query: ({ email }) => ({
        url: '/auth/student/resend-confirmation',
        method: 'POST',
        body: { email },
      }),
      transformResponse: (response: SuccessApiResponse<IResendVerificationEmailResponse>) =>
        response.data,
    }),
    forgotPassword: build.mutation<IForgotPasswordResponse, IForgotPasswordRequest>({
      query: ({ email }) => ({
        url: '/auth/forgot',
        method: 'POST',
        body: { email, clientType: 'lms' },
      }),
    }),
    resetPassword: build.mutation<IAuthResponse, IResetPasswordRequest>({
      query: ({ code, password }) => ({
        url: '/auth/reset',
        method: 'POST',
        body: { code, password },
      }),
      transformResponse: (response: SuccessApiResponse<IAuthResponse>) =>
        saveAuthData(response.data),
    }),
    // Гасит сессию на всех устройствах. Звать до очистки локального состояния:
    // Authorization для запроса берётся из storage.
    logout: build.mutation<ILogoutResponse, ILogoutRequest | void>({
      query: (body) => ({
        url: '/auth/logout',
        method: 'POST',
        ...(body ? { body } : {}),
      }),
      // Сессия могла умереть раньше. Редиректом на /login управляет вызывающий,
      // иначе перехватчик перебьёт наш выход хардовым window.location.
      extraOptions: { skipAuthRedirect: true },
      transformResponse: (response: SuccessApiResponse<ILogoutResponse>) => response.data,
    }),
  }),
});

export const {
  useSignInMutation,
  useConfirmEmailMutation,
  useSetPasswordMutation,
  useGetActivationQuery,
  useActivateAccountMutation,
  useResendVerificationEmailMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useLogoutMutation,
} = authApi;
